/**
 * Brand Login Preview — public full-page route (spec 0014).
 *
 * Reads brand config from localStorage (handed off from the Brand Editor's
 * Preview button) and delegates rendering to BrandLoginMock (extracted in 0019).
 * No API calls, no auth required; public route contract frozen.
 */
import { useState } from 'react';
import { Alert, Box } from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { BrandLoginMock } from './BrandLoginMock';

const STORAGE_KEY = 'brand-login-preview';

export interface BrandPreviewConfig {
  displayName: string;
  shortName?: string;
  logoUrl?: string;
  colors: Record<string, string>;
  fonts: Record<string, string>;
  template?: 'centered' | 'split-hero' | 'backdrop' | '';
  tagline?: string;
  heroCopy?: string;
  heroImageUrl?: string;
  primaryColorHover?: string;
  authProviders: string[];
  signupEnabled: boolean;
  passwordResetEnabled: boolean;
  copyStrings?: Record<string, string>;
}

function readPreviewConfig(): BrandPreviewConfig | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as BrandPreviewConfig;
  } catch {
    return null;
  }
}

export function BrandLoginPreview() {
  const { t } = useCopy();
  // Read the (pure) preview config once via a lazy initializer instead of a
  // mount effect that synchronously setStates — same value, no extra render.
  const [config] = useState<BrandPreviewConfig | null>(() => readPreviewConfig());

  if (!config) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Alert severity="info">
          {t('brands.preview.noConfig', 'No preview config found. Open this from the Brand Editor.')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Preview banner */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bgcolor: 'warning.light',
          color: 'warning.contrastText',
          py: 0.5,
          px: 2,
          textAlign: 'center',
          fontSize: '0.875rem',
          zIndex: 9999,
        }}
      >
        {t('brands.preview.banner', 'Preview — not a working login')}
      </Box>

      {/* Delegate rendering to the mock */}
      <Box sx={{ pt: 6 }}>
        <BrandLoginMock config={config} />
      </Box>
    </Box>
  );
}

